import { Injectable, inject } from '@angular/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';

// Import the functions you need from the SDKs you need
import { getApp, initializeApp } from 'firebase/app';
import { Auth, getAuth } from 'firebase/auth';
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore';
import { AuthRepository } from 'src/app/auth/state/auth.repository';

import { Capacitor } from '@capacitor/core';
import { HistoryRepository } from 'src/app/analytics/state/history.repository';
import { FirebaseSyncService } from '../firebase-sync/firebase-sync.service';
import { DialogService } from '../dialog/dialog.service';
import { FirestoreApiService } from './firestore-api.service';

// start(uid) is normally called once per sign-in, but a transient failure (offline at launch, plugin
// not yet ready) would otherwise leave the device unsynced until the next auth event. Retry a
// bounded number of times with widening spacing; the retry is abandoned the moment the signed-in
// user changes. Two entries => up to three total start attempts (initial + two retries).
const START_RETRY_DELAYS_MS = [5000, 30000];

@Injectable({
  providedIn: 'root',
})
export class FirebaseService {
  private auth = inject(AuthRepository);
  private history = inject(HistoryRepository);
  private dialog = inject(DialogService);
  private sync = inject(FirebaseSyncService);
  private firestoreApi = inject(FirestoreApiService);

  firebaseAuth: Auth | null = null;

  // True once the user has signed out (or started signed-out) during this session, so the next
  // sign-in shows the "Logged In!" toast exactly once. Stays false on a restart with a persisted
  // user (user$ replays that user first), so a normal app launch never toasts.
  private didLoginInThisSession = false;

  async init() {
    // WEB ONLY: bootstrap the Firebase JavaScript SDK. Native uses the Capacitor plugin directly
    // and needs no SDK init (native Firestore persistence is enabled by default).
    if (!Capacitor.isNativePlatform()) {
      /**
       * Only needed if the Firebase JavaScript SDK is used.
       *
       * Read more: https://github.com/robingenz/capacitor-firebase/blob/main/packages/authentication/docs/firebase-js-sdk.md
       */
      // Your web app's Firebase configuration
      const firebaseConfig = {
        apiKey: 'AIzaSyBDHZH3-RkJo3-qTsFRI1rPFzInXcTFFXI',
        authDomain: 'practical-focus-af6c8.firebaseapp.com',
        databaseURL: 'https://practical-focus-af6c8-default-rtdb.europe-west1.firebasedatabase.app',
        projectId: 'practical-focus-af6c8',
        storageBucket: 'practical-focus-af6c8.appspot.com',
        messagingSenderId: '1095975476716',
        appId: '1:1095975476716:web:2b003aa6fdde5a9da6f4c9',
      };

      // Initialize Firebase
      initializeApp(firebaseConfig);
      this.firebaseAuth = getAuth();
      // Enable web offline persistence. The Capacitor Firestore plugin's web implementation
      // resolves getFirestore() per call, so it picks up this configured instance.
      initializeFirestore(getApp(), {
        localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
      });
    }

    // COMMON (web + native): auth listener and sync engine lifecycle.
    FirebaseAuthentication.addListener('authStateChange', (change) => {
      // Handle changes in the user's sign-in state
      this.auth.setUser(change.user);
    });

    this.auth.user$.subscribe((user) => {
      if (user) {
        if (this.didLoginInThisSession) {
          this.dialog.showToast({
            title: 'Logged In!',
            message: 'Your timer is now available across devices',
            duration: 4000,
            icon: 'checkmark-circle',
            variant: 'success',
          });
          this.didLoginInThisSession = false;
        }
        this.startSyncWithRetry(user.uid);
      } else {
        this.didLoginInThisSession = true;
        void this.sync.stop();
      }
    });
  }

  /**
   * Starts the sync engine for `uid`, retrying a bounded number of times on failure. Each retry is
   * abandoned if the signed-in user has since changed (or signed out), so a stale retry can never
   * start the engine for a user who is no longer active. start() itself is idempotent, so a
   * redundant retry after a late success is a harmless no-op.
   */
  private startSyncWithRetry(uid: string, attempt = 0): void {
    this.sync.start(uid).catch((error) => {
      console.error('Failed to start sync', error);
      if (this.auth.getUser()?.uid !== uid || attempt >= START_RETRY_DELAYS_MS.length) {
        return;
      }
      setTimeout(() => {
        if (this.auth.getUser()?.uid === uid) {
          this.startSyncWithRetry(uid, attempt + 1);
        }
      }, START_RETRY_DELAYS_MS[attempt]);
    });
  }

  async logout() {
    await this.sync.stop();
    await FirebaseAuthentication.signOut();
    this.history.clearSyncedDays();
  }

  async deleteAccount() {
    if (!this.auth.isAuthenticated()) {
      return;
    }
    const uid = this.auth.getUser()!.uid;

    // Stop the sync engine first so it can't race a save against the deletion below.
    await this.sync.stop();

    // Firestore doesn't cascade deletes: the day-log subcollection under timer/{uid} has to be
    // enumerated and cleared entry-by-entry before the parent doc goes, or every past day's data
    // is orphaned (and retained) after the account is gone. No batch API on the plugin.
    const entries = await this.firestoreApi.getCollection(`timer/${uid}/entries`);
    for (const snapshot of entries?.snapshots ?? []) {
      await this.firestoreApi.deleteDocument(`timer/${uid}/entries/${snapshot.id}`);
    }

    await this.firestoreApi.deleteDocument(`user_settings/${uid}`);
    await this.firestoreApi.deleteDocument(`timer/${uid}`);

    // Delete the auth user via the cross-platform plugin. The web-only `firebaseAuth.currentUser`
    // field is a silent no-op on native, so it must not be used here. A failure (e.g. the plugin
    // requires a recent login) propagates to the caller as before — re-auth UX is a tracked
    // follow-up, not handled here.
    await this.deleteAuthUser();

    this.auth.setUser(null);
    this.history.reset();
  }

  /** Isolated seam over the Capacitor plugin (whose proxy can't be spied) so tests can stub it. */
  private deleteAuthUser(): Promise<void> {
    return FirebaseAuthentication.deleteUser();
  }
}
